import { useState } from 'react'

import { useDebounce } from '@domain-group/react-hooks'

import useAdAccountGroupLink from '@hooks/useAdAccountGroupLink'
import useAdAccountGroupLinks from '@hooks/useAdAccountGroupLinks'
import useClients from '@hooks/useClients'
import useSelector from '@hooks/useSelector'
import useSetState from '@hooks/useSetState'
import useUsers from '@hooks/useUsers'

import type { AdAccountGroupLinkModel } from '@models/types'

const defaultState = {
  entityName: '',
}

type UseBulkCreateAdAccountGroupLinksParams = {
  adAccountGroupLink: Partial<AdAccountGroupLinkModel>,
}

const useBulkCreateAdAccountGroupLinks = (params: UseBulkCreateAdAccountGroupLinksParams) => {
  const {
    adAccountGroupLink,
    adAccountGroupLink: {
      adAccountGroupId,
      linkableType,
    },
  } = params

  const [selectedIds, setSelectedIds] = useState<number[]>([])

  const [state, setState] = useSetState(defaultState)
  const { entityName } = state

  const debouncedString = useDebounce(entityName, 200)

  const isShowingClients = linkableType === 'Client'

  const clientsPayload = useClients({
    filters: {
      string: debouncedString,
    },
    performHttpRequests: isShowingClients,
  })

  const {
    callbacks: {
      loadMore: loadMoreClients,
    },
    canLoadMore: canLoadMoreClients,
    filteredClients,
    loading: loadingClients,
  } = clientsPayload

  // Exclude Admin Users because Admins cannot have AdAccounts linked
  const usersPayload = useUsers({
    excludeAdminUsers: true,
    filters: {
      string: debouncedString,
    },
    performHttpRequests: !isShowingClients,
  })

  const {
    callbacks: {
      loadMore: loadMoreUsers,
    },
    canLoadMore: canLoadMoreUsers,
    filteredUsers,
    loading: loadingUsers,
  } = usersPayload

  const { clients, users } = useSelector(reduxState => reduxState.entities)

  const adAccountGroupLinksPayload = useAdAccountGroupLinks({
    filters: {
      adAccountGroupId,
      linkableType,
    },
  })

  const { filteredAdAccountGroupLinks } = adAccountGroupLinksPayload

  const assignedEntityIds = filteredAdAccountGroupLinks.map(adAccountGroupLink => adAccountGroupLink.linkableId)

  const filteredEntities = isShowingClients ? filteredClients : filteredUsers
  const filteredEntityIds = filteredEntities.reduce((acc, value) => {
    if (!assignedEntityIds.includes(value.id)) {
      acc.push(value.id)
    }

    return acc
  }, [] as number[])

  const {
    callbacks: {
      bulkCreateAdAccountGroupLinks,
    },
    creating,
  } = useAdAccountGroupLink(adAccountGroupLink)

  const entityParams = {
    adAccountGroupIds: [adAccountGroupId],
    linkableIds: selectedIds,
    linkableType,
  }

  return {
    callbacks: {
      bulkCreateAdAccountGroupLinks,
      loadMore: isShowingClients ? loadMoreClients : loadMoreUsers,
      setSelectedIds,
      setState,
    },
    canLoadMore: isShowingClients ? canLoadMoreClients : canLoadMoreUsers,
    creating,
    entities: isShowingClients ? clients : users,
    entityName,
    entityParams,
    filteredEntityIds,
    filteredEntityIdsCount: filteredEntityIds.length,
    isShowingClients,
    loading: isShowingClients ? loadingClients : loadingUsers,
    saveEnabled: !!selectedIds.length,
    selectedIds,
  }
}

export default useBulkCreateAdAccountGroupLinks